import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { AppBar, Box, Toolbar, IconButton, Typography, InputBase, Badge, MenuItem, Menu, Button, Avatar, ListItemIcon } from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingBasketRoundedIcon from '@mui/icons-material/ShoppingBasketRounded';
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded';
import Logout from '@mui/icons-material/Logout';
import LocalMallRoundedIcon from '@mui/icons-material/LocalMallRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';

import { getFirstLetter, getItem, removeAll } from '../utils/Helper';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  border: '2px solid #1d9e5e',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '90%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: '#37474f',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '50ch',
    },
  },
}));

const TopNavigationBar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [searchText, setSearchText] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // get searched text from url if the user reload the page
  useEffect(() => {
    let urlLocation = pathname;
    if (urlLocation.split('/')[1] === 'search') {
      urlLocation = urlLocation.split('/')[2];
      urlLocation = urlLocation.replace(/%20/g, ' ');
      setSearchText(urlLocation);
    } else {
      setSearchText('');
    }
  }, [pathname]); // if user goes to another page then searchText will be empty

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const logout = () => {
    removeAll(localStorage);
    navigate('/');
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    navigate(`/search/${e.target.value}`);
  };

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      id='account-menu'
      open={open}
      onClose={handleClose}
      onClick={handleClose}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: 'visible',
          filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
          mt: 1.5,
          '& .MuiAvatar-root': {
            width: 32,
            height: 32,
            ml: -0.5,
            mr: 1,
          },
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
          },
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
      <MenuItem component={Link} to='/profile'>
        <ListItemIcon>
          <AccountCircleRoundedIcon fontSize='small' />
        </ListItemIcon>
        Profile
      </MenuItem>
      <MenuItem component={Link} to='/purchased-dataset'>
        <ListItemIcon>
          <ShoppingBasketRoundedIcon fontSize='small' />
        </ListItemIcon>
        Purchased dataset
      </MenuItem>
      <MenuItem component={Link} to='/order-history'>
        <ListItemIcon>
          <RestoreRoundedIcon fontSize='small' />
        </ListItemIcon>
        Order history
      </MenuItem>
      <MenuItem component={Link} to='/my-wishlist'>
        <ListItemIcon>
          <FavoriteRoundedIcon fontSize='small' />
        </ListItemIcon>
        Wishlist
      </MenuItem>
      <MenuItem component={Link} to='/my-tickets'>
        <ListItemIcon>
          <ConfirmationNumberRoundedIcon fontSize='small' />
        </ListItemIcon>
        My tickets
      </MenuItem>
      <MenuItem onClick={logout} sx={{ color: 'red' }}>
        <ListItemIcon>
          <Logout color='error' fontSize='small' />
        </ListItemIcon>
        Logout
      </MenuItem>
    </Menu>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position='static' sx={{ backgroundColor: '#f6f6f6' }}>
        <Toolbar>
          <Typography variant='h6' noWrap component={Link} to='/' className='link'>
            Data Lagbe
          </Typography>
          <Search>
            <SearchIconWrapper>
              <SearchIcon color='primary' />
            </SearchIconWrapper>
            <StyledInputBase type='search' placeholder='Search…' inputProps={{ 'aria-label': 'search' }} value={searchText} onChange={handleSearchChange} />
          </Search>
          <Box sx={{ flexGrow: 1 }} />

          <IconButton color='common' component={Link} to='/cart'>
            <Badge color='error' badgeContent={0}>
              <LocalMallRoundedIcon />
            </Badge>
          </IconButton>
          {getItem(localStorage, 'at') ? (
            <IconButton onClick={handleClick} size='small' sx={{ ml: 2 }} aria-controls={open ? 'account-menu' : undefined} aria-haspopup='true' aria-expanded={open ? 'true' : undefined}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: '#656565' }}>{getFirstLetter(getItem(localStorage, 'fn'))}</Avatar>
            </IconButton>
          ) : (
            <Button variant='text' to='/login' component={Link}>
              Login
            </Button>
          )}

          {renderMenu}
        </Toolbar>
      </AppBar>
    </Box>
  );
};

export default TopNavigationBar;
